import { mapActive } from "../../utils/map-utils";
import { RootStore } from "../../../shared/stores/RootStore";
import { Feature, LineString, Polygon, Position } from "geojson";
import { BBoxSelection } from "../BBoxSelection";
import { CompositeCategory } from "../../../survey/models/CompositeCategory";
import turf from "../../../shared/utils/turf";
import { MapMode } from "../../../survey/stores/MapStore";
import { importHlaMapConfig } from "./config";
import { extractCompositeCategories } from "./from-bbox/extractCompositeCategories";
import { queryRoadAndPathLayers } from "./from-bbox/queryRoadAndPathLayers";
import { importHlaFromAToB } from "./from-a-to-b/importHlaFromAToB";
import { importSelectedCategories } from "./from-bbox/importSelectedCategories";
import { importHlaFromStreetview } from "./from-streetview/importHlaFromStreetview";

type BBox = [mapboxgl.Point, mapboxgl.Point];
type Side = "left" | "right";

const config = importHlaMapConfig;

export const originColor = "#1ed4a0";
export const destinationColor = "#b880ec";

export class ImportHlaMapSource {
  map: mapboxgl.Map;
  rootStore: RootStore;
  interactions: string[] = [];
  bboxSelection?: BBoxSelection;
  bbox?: BBox;
  featuresInSelection: Feature<LineString>[] = [];
  activeLayers: string[] = [];

  rightHandTraffic = true;
  frontSide: Side = "right";
  backSide: Side = "left";

  constructor(map: mapboxgl.Map, rootStore: RootStore) {
    this.map = map;
    this.rootStore = rootStore;

    this.map.addLayer(config.parkingLayer);
    this.map.addLayer(config.busStopLayer);
  }

  clear() {
    this.map.removeLayer(config.parkingLayer.id);
    this.map.removeLayer(config.busStopLayer.id);
    this.disable();
  }

  disable() {
    this.bbox = undefined;
    this.featuresInSelection = [];
    this.clearInteractions();
    this.clearActiveLayers();
    this.removeOriginDestinationSource();

    if (this.map.getSource(config.sourceName)) {
      this.map.removeLayer(config.fillLayer.id);
      this.map.removeLayer(config.lineLayer.id);
      this.map.removeSource(config.sourceName);
    }
  }

  clearInteractions() {
    this.interactions.map((name) => this.map.removeInteraction(name));

    this.bboxSelection?.disable();
    this.bboxSelection = undefined;
  }

  onModeChange(mode: MapMode) {
    this.disable();
    if (mode === MapMode.ImportHlaBBox) {
      this.enableBBoxInteraction();
    } else if (mode === MapMode.ImportHlaAtoB) {
      this.addOriginDestinationSource();
    }
  }

  applySurveyConfig() {
    const { surveyStore } = this.rootStore;

    this.rightHandTraffic = surveyStore.rightHandTraffic;
    this.frontSide = surveyStore.rightHandTraffic ? "right" : "left";
    this.backSide = surveyStore.rightHandTraffic ? "left" : "right";
  }

  enableBBoxInteraction() {
    const { mapQueryStore } = this.rootStore;

    this.applySurveyConfig();

    if (mapActive(this.map) && !this.map.getSource(config.sourceName)) {
      this.map.addSource(config.sourceName, config.source);
      this.map.addLayer(config.fillLayer);
      this.map.addLayer(config.lineLayer);
    }

    this.bboxSelection = new BBoxSelection(this.map, {
      onDrag: (bbox) => {
        this.setBBox(bbox);
        this.featuresInSelection = queryRoadAndPathLayers(bbox, this.map);

        const categories = extractCompositeCategories(this.featuresInSelection);
        mapQueryStore.setCompositeCategories(categories);
      },
      onClick: (point) => {
        this.setSourceData(undefined);
      },
    });

    this.bboxSelection.enable();
  }

  private clearActiveLayers() {
    this.activeLayers.forEach((layerName) => {
      this.map.removeLayer(layerName);
    });
    this.activeLayers = [];
  }

  highlightCategories(categories: CompositeCategory[]) {
    this.clearActiveLayers();

    categories.forEach((category) => {
      if (category.enabled) {
        category.items.forEach((item) => {
          const layerId = `highlight-${item.class}-${item.type}`;
          this.activeLayers.push(layerId);
          this.map.addLayer(
            buildHighlightLayerSpec(layerId, category.color, [
              "all",
              ["==", "class", item.class],
              ["==", "type", item.type],
            ]),
          );
        });
      }
    });
  }

  async importSelectedCategories() {
    if (!this.bbox) return;

    importSelectedCategories(
      this.featuresInSelection,
      this.rootStore,
      this.map,
      {
        frontSide: this.frontSide,
        backSide: this.backSide,
        rightHandTraffic: this.rightHandTraffic,
      },
    );
  }

  addOriginDestinationSource() {
    if (
      mapActive(this.map) &&
      !this.map.getSource(config.originDestinationSource)
    ) {
      this.map.addSource(config.originDestinationSource, config.source);
      this.map.addLayer(config.routeStrokeLayer);
      this.map.addLayer(config.routeLineLayer);
      this.map.addLayer(config.originDestinationStrokeLayer);
      this.map.addLayer(config.originDestinationLayer);
      this.map.addLayer(config.originDestinationTextLayer);
    }
  }

  removeOriginDestinationSource() {
    if (this.map.getSource(config.originDestinationSource)) {
      this.map.removeLayer(config.routeStrokeLayer.id);
      this.map.removeLayer(config.routeLineLayer.id);
      this.map.removeLayer(config.originDestinationStrokeLayer.id);
      this.map.removeLayer(config.originDestinationLayer.id);
      this.map.removeLayer(config.originDestinationTextLayer.id);

      this.map.removeSource(config.originDestinationSource);
    }
  }

  awaitPointSelection(onSelection: (point: mapboxgl.LngLat) => void) {
    this.applySurveyConfig();

    const canvas = this.map.getCanvasContainer();
    canvas.style.cursor = "pointer";

    const interactionId = "set-point";

    this.interactions.push(interactionId);
    this.map.removeInteraction(interactionId);
    this.map.addInteraction(interactionId, {
      type: "click",
      handler: (e) => {
        const lnglat = this.map.unproject(e.point);
        onSelection(lnglat);
        canvas.style.cursor = "grab";
        this.map.removeInteraction(interactionId);
      },
    });
  }

  async importFromAToB(route: Position[]) {
    return await importHlaFromAToB(route, this.rootStore, this.map, {
      frontSide: this.frontSide,
      backSide: this.backSide,
      rightHandTraffic: this.rightHandTraffic,
    });
  }

  async importFromStreetview(position: Position, heading: number) {
    return await importHlaFromStreetview(
      position,
      heading,
      this.rootStore,
      this.map,
      {
        frontSide: this.frontSide,
        backSide: this.backSide,
        rightHandTraffic: this.rightHandTraffic,
      },
    );
  }

  private setBBox(bbox: BBox) {
    this.bbox = bbox;

    const { mapQueryStore } = this.rootStore;
    const [pointA, pointB] = bbox;

    const coordsA = this.map.unproject([pointA.x, pointA.y]);
    const coordsB = this.map.unproject([pointB.x, pointB.y]);

    const polygon: Polygon = {
      type: "Polygon",
      coordinates: [
        [
          [coordsA.lng, coordsA.lat],
          [coordsB.lng, coordsA.lat],
          [coordsB.lng, coordsB.lat],
          [coordsA.lng, coordsB.lat],
          [coordsA.lng, coordsA.lat],
        ],
      ],
    };

    mapQueryStore.setPolygon(polygon.coordinates[0]); // TODO can this be removed?

    this.setSourceData(polygon);
  }

  setSourceData(geometry: Polygon | LineString | undefined) {
    if (mapActive(this.map)) {
      const source = this.map.getSource(
        config.sourceName,
      ) as mapboxgl.GeoJSONSource;
      source?.setData({
        type: "FeatureCollection",
        features: geometry
          ? [
              {
                type: "Feature",
                geometry,
                properties: {},
              },
            ]
          : [],
      });
    }
  }

  setOriginDestinationData(
    origin: mapboxgl.LngLat | undefined,
    destination: mapboxgl.LngLat | undefined,
    routes: LineString[],
    selectedRouteIndex: number,
  ) {
    if (mapActive(this.map)) {
      const source = this.map.getSource(
        config.originDestinationSource,
      ) as mapboxgl.GeoJSONSource;

      const features: Feature[] = [];

      if (origin) {
        features.push(
          turf.point([origin.lng, origin.lat], {
            color: originColor,
            text: "A",
          }),
        );
      }

      if (destination) {
        features.push(
          turf.point([destination.lng, destination.lat], {
            color: destinationColor,
            text: "B",
          }),
        );
      }

      routes.forEach((route, index) => {
        features.push(
          turf.lineString(route.coordinates, {
            selected: index === selectedRouteIndex,
          }),
        );
      });

      source?.setData({
        type: "FeatureCollection",
        features: features,
      });
    }
  }
}

function buildHighlightLayerSpec(
  id: string,
  color: string,
  filter: mapboxgl.FilterSpecification,
): mapboxgl.LineLayerSpecification {
  return {
    id,
    filter,
    type: "line",
    source: "composite",
    layout: {
      "line-join": "round",
      "line-cap": "round",
    },
    paint: {
      "line-color": color,
      "line-width": 4,
      "line-opacity": 0.8,
    },
    "source-layer": "road",
  };
}

// const testLayerName = `test`;
// const testLayerConfig: mapboxgl.LineLayerSpecification = {
//   type: "line",
//   layout: {
//     "line-join": "round",
//     "line-cap": "round",
//   },
//   paint: {
//     "line-color": "#b81fe7",
//     "line-width": 5,
//   },
//   id: testLayerName,
//   source: "composite",
//   "source-layer": "road",
//   filter: ["all", isLineString, ["==", "type", "residential"]],
// };
